import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const DatePicker = ({
  // Single-date props
  value,
  onChange,
  // Range props
  selectsRange = false,
  startDate: rangeStart,
  endDate: rangeEnd,
  onRangeChange,
  // External control props (for headless usage)
  open,
  onOpenChange,
  anchorRef,
  // Shared props
  isDateAllowed = () => true,
  minDate,
  maxDate,
  availableDates = [],
  disablePastDates = true,
  inputClassName,
}) => {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const showCalendar = isControlled ? open : internalOpen;
  const setShowCalendar = isControlled
    ? (v) => onOpenChange?.(v)
    : setInternalOpen;

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (selectsRange && rangeStart) return moment(rangeStart);
    return value ? moment(value) : moment();
  });
  const [hoverDate, setHoverDate] = useState(null);
  const [pendingStart, setPendingStart] = useState(null);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => currentMonth.year());

  const datePickerRef = useRef(null);
  const triggerRef = useRef(null);
  const calendarRef = useRef(null);

  const positionRef = anchorRef || triggerRef;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const wrapperRef = isControlled ? { current: null } : datePickerRef;
      const anchor = anchorRef?.current;
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        (!wrapperRef.current || !wrapperRef.current.contains(event.target)) &&
        (!anchor || !anchor.contains(event.target))
      ) {
        setShowCalendar(false);
        setPendingStart(null);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar, isControlled, anchorRef, setShowCalendar]);

  useEffect(() => {
    if (!showCalendar || !positionRef.current) return;
    const CALENDAR_HEIGHT = 340;
    const update = () => {
      const rect = positionRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < CALENDAR_HEIGHT
        ? rect.top - CALENDAR_HEIGHT - 4
        : rect.bottom + 4;
      setDropdownPos({ top, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showCalendar, positionRef]);

  const generateCalendarDays = useCallback(() => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const calStart = startOfMonth.clone().startOf("isoWeek");
    const calEnd = endOfMonth.clone().endOf("isoWeek");

    const days = [];
    let day = calStart.clone();
    const today = moment().startOf("day");
    const minMoment = minDate ? moment(minDate).startOf("day") : null;
    const maxMoment = maxDate ? moment(maxDate).startOf("day") : null;

    // Range boundaries
    const rStart = selectsRange
      ? (pendingStart ? moment(pendingStart).startOf("day") : (rangeStart ? moment(rangeStart).startOf("day") : null))
      : null;
    const rEnd = selectsRange
      ? (pendingStart ? null : (rangeEnd ? moment(rangeEnd).startOf("day") : null))
      : null;
    const hover = selectsRange && pendingStart && hoverDate
      ? moment(hoverDate).startOf("day")
      : null;

    while (day.isSameOrBefore(calEnd)) {
      const formattedDate = day.format("YYYY-MM-DD");

      const isWithinRange =
        (!minMoment || day.isSameOrAfter(minMoment)) &&
        (!maxMoment || day.isSameOrBefore(maxMoment));

      const isAvailableDate =
        availableDates.length === 0 || availableDates.includes(formattedDate);

      const isAvailable =
        isWithinRange &&
        isAvailableDate &&
        (isDateAllowed ? isDateAllowed(formattedDate) : true);

      const dayStart = day.clone().startOf("day");

      let isRangeStart = false;
      let isRangeEnd = false;
      let isInRange = false;

      if (selectsRange) {
        if (rStart && rEnd) {
          isRangeStart = dayStart.isSame(rStart, "day");
          isRangeEnd = dayStart.isSame(rEnd, "day");
          isInRange = dayStart.isAfter(rStart) && dayStart.isBefore(rEnd);
        } else if (rStart && hover) {
          const effectiveEnd = hover.isAfter(rStart) ? hover : rStart;
          const effectiveStart = hover.isAfter(rStart) ? rStart : hover;
          isRangeStart = dayStart.isSame(effectiveStart, "day");
          isRangeEnd = dayStart.isSame(effectiveEnd, "day");
          isInRange = dayStart.isAfter(effectiveStart) && dayStart.isBefore(effectiveEnd);
        } else if (rStart) {
          isRangeStart = dayStart.isSame(rStart, "day");
        }
      }

      days.push({
        date: day.clone(),
        isCurrentMonth: day.month() === currentMonth.month(),
        isAvailable,
        isSelected: !selectsRange && value === formattedDate,
        isToday: day.isSame(today, "day"),
        isRangeStart,
        isRangeEnd,
        isInRange,
      });

      day.add(1, "day");
    }

    return days;
  }, [currentMonth, isDateAllowed, minDate, maxDate, value, availableDates, selectsRange, rangeStart, rangeEnd, pendingStart, hoverDate]);

  const handleDateClick = (day) => {
    if (!day.isAvailable) return;
    const formattedDate = day.date.format("YYYY-MM-DD");

    if (selectsRange) {
      if (!pendingStart) {
        setPendingStart(formattedDate);
        setHoverDate(null);
      } else {
        const start = moment(pendingStart);
        const end = moment(formattedDate);
        if (end.isBefore(start)) {
          setPendingStart(formattedDate);
          setHoverDate(null);
        } else {
          onRangeChange?.(pendingStart, formattedDate);
          setPendingStart(null);
          setHoverDate(null);
          setShowCalendar(false);
        }
      }
    } else {
      onChange(formattedDate);
      setShowCalendar(false);
    }
  };

  const handleDayHover = (day) => {
    if (selectsRange && pendingStart && day.isCurrentMonth) {
      setHoverDate(day.date.format("YYYY-MM-DD"));
    }
  };

  const handlePrevMonth = () => {
    const prevMonth = currentMonth.clone().subtract(1, "month");
    if (
      availableDates.length === 0 ||
      availableDates.some((date) => moment(date).month() === prevMonth.month())
    ) {
      setCurrentMonth(prevMonth);
    }
  };

  const handleNextMonth = () => {
    const nextMonth = currentMonth.clone().add(1, "month");
    if (!maxDate || nextMonth.isSameOrBefore(moment(maxDate), "month")) {
      setCurrentMonth(nextMonth);
    }
  };

  const getDayClassName = (day) => {
    if (!day.isCurrentMonth) return "text-[#7F8585]/30";
    if (day.isRangeStart || day.isRangeEnd || day.isSelected) {
      return "bg-[#00E7FF] text-[#021529] font-bold";
    }
    if (day.isInRange) return "bg-[#00E7FF]/20 text-[#FEFCEB]";
    if (day.isToday) return "border border-blue-shade-2 text-blue-shade-2 font-medium";
    if (day.isAvailable) return "text-[#FEFCEB] hover:bg-white/10";
    return "text-[#7F8585]/40 cursor-not-allowed";
  };

  const calendar = showCalendar && createPortal(
    <div
      ref={calendarRef}
      style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, width: 288, zIndex: 9999 }}
      className="bg-[#021529] border border-[#192634] rounded-2xl shadow-xl p-4"
    >
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrevMonth}
          disabled={minDate && currentMonth.isSameOrBefore(moment(minDate), "month")}
          className="w-8 h-8 flex items-center justify-center text-[#FEFCEB] hover:opacity-50 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
        >
          <FiChevronLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="flex items-center gap-1 hover:opacity-70 transition-opacity"
          onClick={() => { setPickerYear(currentMonth.year()); setShowMonthYearPicker(!showMonthYearPicker); }}
        >
          <span className="text-[#FEFCEB] text-[15px] font-medium">{currentMonth.format("MMM YYYY")}</span>
          <FiChevronDown className={`w-3.5 h-3.5 text-[#FEFCEB] transition-transform ${showMonthYearPicker ? "rotate-180" : ""}`} />
        </button>
        <button
          onClick={handleNextMonth}
          disabled={maxDate && currentMonth.isSameOrAfter(moment(maxDate), "month")}
          className="w-8 h-8 flex items-center justify-center text-[#FEFCEB] hover:opacity-50 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
        >
          <FiChevronRight className="w-4 h-4" />
        </button>
      </div>

      {showMonthYearPicker ? (
        <>
          {/* Year navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setPickerYear((y) => y - 1)}
              className="w-8 h-8 flex items-center justify-center text-[#FEFCEB] hover:opacity-50 transition-opacity"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[#FEFCEB] text-[15px] font-medium">{pickerYear}</span>
            <button
              type="button"
              onClick={() => setPickerYear((y) => y + 1)}
              className="w-8 h-8 flex items-center justify-center text-[#FEFCEB] hover:opacity-50 transition-opacity"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-2">
            {moment.monthsShort().map((m, i) => {
              const isSelected = currentMonth.month() === i && currentMonth.year() === pickerYear;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setCurrentMonth(moment().year(pickerYear).month(i).startOf("month"));
                    setShowMonthYearPicker(false);
                  }}
                  className={`py-2 rounded-full text-[13px] font-medium transition-colors ${
                    isSelected
                      ? "bg-[#00E7FF] text-[#021529]"
                      : "text-[#FEFCEB] hover:bg-white/10"
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="text-center text-[12px] text-[#7F8585] font-medium py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7">
            {generateCalendarDays().map((day, index) => (
              <div
                key={index}
                className={`aspect-square flex items-center justify-center text-[13px] rounded-full cursor-pointer transition-colors ${getDayClassName(day)}`}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => handleDayHover(day)}
              >
                {day.date.date()}
              </div>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );

  // Headless mode — no trigger, just the calendar portal
  if (isControlled) {
    return calendar || null;
  }

  return (
    <div className="relative w-full" ref={datePickerRef}>
      <div
        ref={triggerRef}
        className="cursor-pointer"
        onClick={() => setShowCalendar(!showCalendar)}
      >
        <input
          type="text"
          value={value ? moment(value).format("DD.MM.YYYY") : ""}
          readOnly
          placeholder="DD.MM.YYYY"
          className={`w-full h-11 px-4 bg-app-surface border rounded-full text-[#FEFCEB] text-[15px] outline-none cursor-pointer placeholder-[#7F8585] transition-colors ${showCalendar ? "border-[#00E7FF]" : "border-transparent"} ${inputClassName || ""}`}
        />
      </div>
      {calendar}
    </div>
  );
};

export default DatePicker;
